import React from 'react';
import { Breakpoint, useIsBreakpoint } from '@/hooks/useIsBreakpoint';
import { useBuilderArticles } from '@/hooks/useBuilderArticles';
import { ArticleCard } from '../ArticleCard/ArticleCard';
import { ArticleCardVariant } from '../ArticleCard/ArticleCard.types';
import {
  FEATURED_ARTICLES_DEFAULT_LIMIT,
  FeaturedArticlesInput,
  FeaturedArticlesLayoutType,
} from './FeaturedArticles.types';
import { CarouselContainer } from '../Carousel/Carousel';

export const FeaturedArticles: React.FC<FeaturedArticlesInput> = (props) => {
  const {
    layout,
    title,
    autoSelectLatest = true,
    limit = FEATURED_ARTICLES_DEFAULT_LIMIT,
    articles: articleInputs = [],
    builderState: { state },
    tagVariant = 'primary',
  } = props;
  const isTablet = useIsBreakpoint(Breakpoint.MD);

  const articles = useBuilderArticles({
    autoSelectLatest,
    limit,
    latestArticles: state.featuredArticles,
    articleInputs,
    categories: state.categories,
  });

  if (!articles.length) {
    return null;
  }

  const renderContent = () => {
    if (layout === FeaturedArticlesLayoutType.Featured && !isTablet) {
      const showArrows = articles.length >= 3;
      /*
        For desktop only, we want to show the first article in a larger card and the next two in smaller cards stacked.
        Mobile will show all articles in a single column.
      */
      return (
        <CarouselContainer
          useCarousel={articles.length >= 2}
          settings={{ slidesToShow: 1, fade: true, stacked: true, responsive: [], showArrows }}
        >
          {articles.map((article, index) => {
            const rightCards = [articles[(index + 1) % articles.length], articles[(index + 2) % articles.length]];

            return (
              <div key={`featured-${index}`}>
                <div className="flex w-full gap-4">
                  <div className="w-1/2">
                    <ArticleCard article={article} tagVariant={tagVariant} />
                  </div>
                  <div className="flex w-1/2 flex-col gap-4">
                    {rightCards.map((rightArticle, rightIndex) => (
                      <ArticleCard
                        key={`right-${index}-${rightIndex}`}
                        article={rightArticle}
                        className="h-1/2 min-h-0"
                        variant={ArticleCardVariant.TwoColumn}
                        tagVariant={tagVariant}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </CarouselContainer>
      );
    }

    return (
      <CarouselContainer
        useCarousel={articles.length >= 2}
        settings={{ slidesToShow: isTablet ? 1 : 3, slidesToScroll: 1, showArrows: true }}
      >
        {articles.map((article, index) => (
          <ArticleCard key={`article-${index}`} article={article} tagVariant={tagVariant} />
        ))}
      </CarouselContainer>
    );
  };

  return (
    <>
      <h2>{title}</h2>
      {renderContent()}
    </>
  );
};
